import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookText, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSpace } from "@/context/SpaceContext";
import { ConfirmButton } from "@/components/admin/ConfirmButton";

const PromptDialog = ({ prompt, onClose, onSaved }) => {
  const { t } = useI18n();
  const [title, setTitle] = useState(prompt?.title || "");
  const [text, setText] = useState(prompt?.text || "");
  const [saving, setSaving] = useState(false);
  const { base } = useSpace();
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (prompt?.id) await api.put(`${base}/prompts/${prompt.id}`, { title, text });
      else await api.post(`${base}/prompts`, { title, text });
      toast.success(t("prompts.saved"));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="card-carbon w-[calc(100vw-2rem)] max-w-lg rounded-2xl" data-testid="prompt-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wider gold-text">{t(prompt?.id ? "prompts.edit" : "prompts.new")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="prompt-title">{t("prompts.name")}</Label><Input id="prompt-title" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="prompt-title-input" /></div>
          <div className="space-y-2"><Label htmlFor="prompt-text">{t("prompts.text")}</Label><Textarea id="prompt-text" required rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="prompt-text-input" /></div>
          <div className="flex justify-end"><button type="submit" className="btn-gold btn-sm" disabled={saving} data-testid="prompt-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("prompts.save")}</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export function usePrompts() {
  const [prompts, setPrompts] = useState([]);
  const { base } = useSpace();
  const load = useCallback(() => api.get(`${base}/prompts`).then(({ data }) => setPrompts(data)).catch(() => {}), [base]);
  useEffect(() => {
    load();
  }, [load]);
  return { prompts, load };
}

export const PromptLibraryCard = () => {
  const { t } = useI18n();
  const { prompts, load } = usePrompts();
  const [editing, setEditing] = useState(null);
  const { base } = useSpace();
  const remove = async (p) => {
    try {
      await api.delete(`${base}/prompts/${p.id}`);
      toast.success(t("prompts.deleted"));
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="prompt-library-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><BookText className="h-5 w-5 text-[var(--gold)]" /> {t("prompts.title")}</h2>
        <button type="button" className="btn-gold btn-sm" onClick={() => setEditing({})} data-testid="prompt-new-btn"><Plus className="h-4 w-4" /></button>
      </div>
      <p className="mt-2 text-sm text-neutral-400">{t("prompts.hint")}</p>
      {prompts.length === 0 && <p className="mt-4 text-xs text-neutral-500" data-testid="prompts-empty">{t("prompts.empty")}</p>}
      <ul className="mt-4 space-y-2">
        {prompts.map((p) => (
          <li key={p.id} className="flex items-center gap-2 rounded-xl border border-[rgba(245,197,66,0.18)] bg-black/30 p-3" data-testid={`prompt-row-${p.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white" data-testid="prompt-row-title">{p.title}</p>
              <p className="truncate text-xs text-neutral-500">{p.text}</p>
            </div>
            <button type="button" className="rounded-full p-2 text-[var(--gold)] hover:bg-white/5" onClick={() => setEditing(p)} aria-label={t("prompts.edit")} data-testid={`prompt-edit-btn-${p.id}`}><Pencil className="h-4 w-4" /></button>
            <ConfirmButton title={t("prompts.deleteTitle")} description={p.title} onConfirm={() => remove(p)} className="rounded-full p-2 text-red-300 hover:bg-white/5" testId={`prompt-delete-btn-${p.id}`}>
              <Trash2 className="h-4 w-4" />
            </ConfirmButton>
          </li>
        ))}
      </ul>
      {editing && <PromptDialog prompt={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </section>
  );
};
